// MACHINERY V2 (Stroje) — V4 shop-floor wall: the header's status counters
// (M2.14; split out of WallHeader.tsx by the 2026-08-24 exit-button ruling,
// which would otherwise have pushed that file past the project's 300-line cap).
//
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png. Reference impl: desktop/machinery-v4-wall.jsx
// (V4WallHeader). NOTHING ABOUT THE COUNTERS CHANGED IN THE MOVE — the same
// specs, the same markup and the same reasoning, relocated verbatim.
//
// THE ALARM COUNTER RENDERS ONLY WHEN > 0 (the ticket says so, and the artboard
// shows it present, at 01). A permanent "00 V ALARME" on a hall TV is a
// standing red numeral that people stop reading; its absence is the signal.
//
// COUNTS COME FROM THE SAME SET THE TILES DO — WallHeader is handed
// countStates(<the machines the grid renders>, states). On a surface with no
// filter rail there is nothing to explain a discrepancy, so a header counting
// machines the grid below does not show would simply be wrong.
//
// SINCE THE M2.14 FIX PASS THAT SET IS THE WHOLE ACTIVE PARK: the wall renders
// `FleetPageData.flat`, the un-faceted base set the rail's own counts are
// computed over (useFleetPageData.ts:154, `countStates(flat, states)`,
// personally re-read at commit time). "Count what we render" and "count the
// plant" are now the same number; handed the FACETED `visibleFlat` they were
// not, and a header reading 01 V ALARME over a hall of five was self-consistent
// and false.
//
// M2.16b — THREE SIZES, ONE COUNTER SET. The wall recomposes below 1280
// (mobile/screenshots 12 and 13) and its numerals shrink 36 -> 26 -> 20, so this
// component takes a `size` instead of being forked into a "mobile counters"
// twin. The default is the wall's, and every value in the `wall` row of
// COUNTER_SIZES is the literal that was hardcoded here before the prop existed,
// so the >= 1280 render is unchanged.
//
// THE VOCABULARY DOES NOT CHANGE WITH THE SIZE, and that is a deliberate
// deviation from the mobile board: 12 and 13 draw FOUR counters, folding
// stopped + offline + unconnected into one "OSTATNÉ". Ours stay the five
// WALL_COUNTERS below. A phone-only bucket would be a SECOND way of counting the
// same fleet — the exact divergence this table exists to prevent — and the row
// wraps rather than crowding, so the space argument the board is answering does
// not arise. `unconnected` is still uncounted at every size, for the reason in
// WALL_COUNTERS' own note.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import StatusIcon from "../StatusIcon";
import { MachineryV2IconKind, MachineryV2StatusToken } from "../cardStates";

interface WallCounterSpec {
  /** Sum of these canonical states (M2.1 contract names). */
  states: string[];
  token: MachineryV2StatusToken;
  icon: MachineryV2IconKind;
  labelKey: string;
  /** Hide the counter entirely at zero (alarm only). */
  hideWhenZero?: boolean;
}

/**
 * The artboard's five counters, mapped onto the 9-state contract.
 *
 * `stale-alarm` is summed into the alarm counter ON PURPOSE: a machine whose
 * alarm reading is last-known is still a machine in alarm, and the wall's job
 * is to make someone walk over. The TILE keeps the distinction (it desaturates
 * and prints the last-known prefix); the counter does not need two numerals for
 * one problem.
 *
 * `unconnected`, `awaiting` and `stale` get NO counter, exactly as the artboard
 * has none: on a real fleet ~99% of machines are unconnected, so a sixth
 * numeral in the hundreds would dwarf the five that matter. All three are still
 * rendered as tiles below.
 */
const WALL_COUNTERS: readonly WallCounterSpec[] = [
  {
    states: ["alarm", "stale-alarm"],
    token: "--red",
    icon: "alert",
    labelKey: "myMachinery:v2.fleet.wall.cAlarm",
    hideWhenZero: true,
  },
  {
    states: ["running"],
    token: "--green",
    icon: "play",
    labelKey: "myMachinery:v2.fleet.wall.cRunning",
  },
  {
    states: ["idle"],
    token: "--amber",
    icon: "pause",
    labelKey: "myMachinery:v2.fleet.wall.cIdle",
  },
  {
    states: ["stopped"],
    token: "--muted",
    icon: "square",
    labelKey: "myMachinery:v2.fleet.wall.cStopped",
  },
  {
    states: ["offline"],
    token: "--faint",
    icon: "cloudoff",
    labelKey: "myMachinery:v2.fleet.wall.cOffline",
  },
];

/** The three renders of the same counter. `wall` holds the values this file
 *  shipped with; the other two are the mobile boards' (12 = phone, 13 = tablet
 *  1024). One table, so a size can never be half-applied. */
const COUNTER_SIZES = {
  wall: { numeral: 36, label: 11, icon: 11, rowGap: 8, gap: 22, pairGap: 8, iconGap: 5 },
  tablet: { numeral: 26, label: 9, icon: 10, rowGap: 6, gap: 18, pairGap: 6, iconGap: 4 },
  phone: { numeral: 20, label: 8.5, icon: 9, rowGap: 6, gap: 14, pairGap: 5, iconGap: 4 },
} as const;

export type WallCounterSize = keyof typeof COUNTER_SIZES;

const WallCounter: React.FC<{
  n: number;
  label: string;
  spec: WallCounterSpec;
  size: (typeof COUNTER_SIZES)[WallCounterSize];
}> = ({ n, label, spec, size }) => {
  const color = "var(" + spec.token + ")";
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: size.pairGap }}>
      {/* Zero-padded to two digits as the artboard prints it — padStart never
          truncates, so a three-digit fleet count still prints all three. */}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: size.numeral,
          color,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {String(n).padStart(2, "0")}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: size.iconGap,
          fontFamily: "var(--font-mono)",
          fontSize: size.label,
          color: "var(--ink-2)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          opacity: 0.75,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ color, display: "inline-flex" }}>
          <StatusIcon kind={spec.icon} size={size.icon} />
        </span>
        {label}
      </span>
    </div>
  );
};

export interface WallCountersProps {
  /** countStates() over the machines the grid renders. */
  counts: Record<string, number>;
  /** Defaults to the 1920 wall's own scale — see COUNTER_SIZES. */
  size?: WallCounterSize;
}

const WallCounters: React.FC<WallCountersProps> = ({ counts, size = "wall" }) => {
  const { translate } = useTranslation("myMachinery");
  const s = COUNTER_SIZES[size];

  return (
    <div
      style={{
        display: "flex",
        gap: s.gap,
        rowGap: s.rowGap,
        flexWrap: "wrap",
        alignItems: "baseline",
      }}
    >
      {WALL_COUNTERS.map((spec) => {
        const n = spec.states.reduce((sum, st) => sum + (counts[st] || 0), 0);
        if (spec.hideWhenZero && n === 0) return null;
        return (
          <WallCounter
            key={spec.labelKey}
            n={n}
            label={translate(spec.labelKey)}
            spec={spec}
            size={s}
          />
        );
      })}
    </div>
  );
};

export default WallCounters;
